import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin, of } from 'rxjs';
import { ACTIVE_FILTER_OPTIONS, WarehousingMasterType, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb, warehousingMasterTypeDescription, warehousingMasterTypeLabel, warehousingMasterTypeRoute } from '../warehousing-common';
import { WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

interface RelationshipFilterOption {
  label: string;
  value: number | string;
}

type SetupListRow = WarehousingMasterData & {
  treeDepth?: number;
  childCount?: number;
};

@Component({
  selector: 'app-warehousing-setup-list',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule,
    TagModule, TooltipModule
  ],
  templateUrl: './warehousing-setup-list.component.html',
  styleUrl: './warehousing-setup-list.component.css'
})
export class WarehousingSetupListComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);

  isLoading = false;
  type: WarehousingMasterType = 'REGION';
  title = 'Regions';
  listRoute = '/setup/regions';
  isStandaloneContext = false;
  headerSubtitle = 'Maintain warehousing setup values in their dedicated setup area.';
  rows: SetupListRow[] = [];
  filteredRows: SetupListRow[] = [];
  relationshipOptions: RelationshipFilterOption[] = [];
  locationOptions: RelationshipFilterOption[] = [];
  supplierOptions: RelationshipFilterOption[] = [];
  supplierWarehouseOptions: RelationshipFilterOption[] = [];
  ownershipOptions: RelationshipFilterOption[] = [];
  first = 0;
  pageSize = 20;
  lastUpdated: Date | null = null;
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  activeFilterOptions = ACTIVE_FILTER_OPTIONS;

  searchForm = this.fb.group({
    name: [''],
    warehouseId: [null as number | null],
    code: [''],
    location: [null as string | null],
    supplier: [null as string | null],
    supplierWarehouse: [null as string | null],
    ownership: [null as string | null],
    relationship: [null as number | string | null],
    activeStatus: [null as string | null]
  });

  ngOnInit(): void {
    this.type = this.route.snapshot.data['masterType'] as WarehousingMasterType;
    this.isStandaloneContext = this.route.snapshot.data['standaloneContext'] === true;
    this.title = warehousingMasterTypeLabel(this.type);
    this.listRoute = warehousingMasterTypeRoute(this.type);
    this.headerSubtitle = warehousingMasterTypeDescription(this.type);
    this.breadcrumbItems = this.isStandaloneContext
      ? [{ label: 'Setup' }, { label: this.title }]
      : warehousingBreadcrumb(this.title, 'Setup', '/warehousing/setup/storage-rates');
    this.loadRows();
  }

  loadRows(): void {
    this.isLoading = true;
    forkJoin({
      rows: this.warehousingService.getMasterData(this.type),
      relationshipRows: this.relationshipSourceType
        ? this.warehousingService.getMasterData(this.relationshipSourceType, { activeStatus: 'ACTIVE' })
        : of(null)
    }).subscribe({
      next: response => {
        this.rows = this.buildDisplayRows(response.rows.data || []);
        this.relationshipOptions = this.buildRelationshipOptions(response.relationshipRows?.data || []);
        if (this.type === 'WAREHOUSE') {
          this.locationOptions = this.buildValueOptions(this.rows.map(row => this.locationLabel(row)));
          this.supplierOptions = this.buildValueOptions(this.rows.map(row => row.supplierClientName));
          this.supplierWarehouseOptions = this.buildValueOptions(this.rows.map(row => row.supplierWarehouseName));
          this.ownershipOptions = this.buildValueOptions(this.rows.map(row => row.warehouseOwnershipType || 'COMPANY'), value => this.ownershipLabel({ warehouseOwnershipType: value }));
        }
        this.lastUpdated = new Date();
        this.applySearch(false);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(resetPage = true): void {
    const filters = this.searchForm.getRawValue();
    this.filteredRows = this.rows.filter(row =>
      (this.type === 'WAREHOUSE' ? !filters.warehouseId || row.id === filters.warehouseId : this.matchesFilter(row.name, filters.name)) &&
      this.matchesFilter(row.code, filters.code) &&
      (!this.showLocationColumn || !filters.location || this.locationLabel(row) === filters.location) &&
      (!this.showSupplierColumn || ((!filters.supplier || row.supplierClientName === filters.supplier) &&
        (!filters.supplierWarehouse || row.supplierWarehouseName === filters.supplierWarehouse) &&
        (!filters.ownership || (row.warehouseOwnershipType || 'COMPANY') === filters.ownership))) &&
      (!this.showRelationshipColumn || this.matchesRelationship(row, filters.relationship)) &&
      this.matchesStatus(row, filters.activeStatus)
    );
    if (resetPage) {
      this.first = 0;
    }
  }

  clearSearch(): void {
    this.searchForm.reset({
      name: '',
      warehouseId: null,
      code: '',
      location: null,
      supplier: null,
      supplierWarehouse: null,
      ownership: null,
      relationship: null,
      activeStatus: null
    });
    this.applySearch();
  }

  statusSeverity = statusSeverity;

  get showLocationColumn(): boolean {
    return this.type === 'WAREHOUSE';
  }

  get showSupplierColumn(): boolean {
    return this.type === 'WAREHOUSE';
  }

  get showRelationshipColumn(): boolean {
    return ['WAREHOUSE', 'ZONE', 'CATEGORY', 'BRAND', 'SUB_BRAND'].includes(this.type);
  }

  get relationshipSourceType(): WarehousingMasterType | null {
    switch (this.type) {
      case 'WAREHOUSE':
        return 'REGION';
      case 'ZONE':
        return 'WAREHOUSE';
      case 'BRAND':
        return 'CATEGORY';
      case 'SUB_BRAND':
        return 'BRAND';
      default:
        return null;
    }
  }

  get relationshipFilterLabel(): string {
    switch (this.type) {
      case 'WAREHOUSE':
        return 'Region';
      case 'ZONE':
        return 'Warehouse';
      case 'CATEGORY':
        return 'Scope / Parent';
      case 'BRAND':
        return 'Category';
      case 'SUB_BRAND':
        return 'Brand';
      default:
        return 'Relationship';
    }
  }

  get activeCount(): number {
    return this.rows.filter(row => row.isActive !== false).length;
  }

  get inactiveCount(): number {
    return this.rows.filter(row => row.isActive === false).length;
  }

  get linkedLocationCount(): number {
    return this.rows.filter(row => !!row.locationId || !!row.locationName).length;
  }

  get relatedCount(): number {
    return this.rows.filter(row => this.relationshipLabel(row) !== '-').length;
  }

  get activeSearchCount(): number {
    return Object.values(this.searchForm.getRawValue()).filter(value => !!String(value || '').trim()).length;
  }

  get singularTitle(): string {
    if (this.title.endsWith('ies')) {
      return `${this.title.slice(0, -3)}y`;
    }
    return this.title.endsWith('s') ? this.title.slice(0, -1) : this.title;
  }

  get codeFallbackPrefix(): string {
    if (this.type === 'WAREHOUSE') return 'WH';
    if (this.type === 'REGION') return 'REG';
    if (this.type === 'TENANT_UOM') return 'UOM';
    return this.type;
  }

  rowCode(row: WarehousingMasterData): string {
    return row.code || `${this.codeFallbackPrefix}-${row.id || '-'}`;
  }

  locationLabel(row: WarehousingMasterData): string {
    return row.locationName || (row.locationId ? `Location #${row.locationId}` : '-');
  }

  ownershipLabel(row: Pick<WarehousingMasterData, 'warehouseOwnershipType'>): string {
    if (row.warehouseOwnershipType === 'THIRD_PARTY_VIRTUAL') return 'Third-party virtual';
    if (row.warehouseOwnershipType === 'THIRD_PARTY_MANAGED') return 'Third-party managed';
    return 'Company owned';
  }

  defaultWarehouseLabel(row: WarehousingMasterData): string {
    return row.defaultWarehouseName || (row.defaultWarehouseId ? `Warehouse #${row.defaultWarehouseId}` : 'Not set');
  }

  relationshipLabel(row: WarehousingMasterData): string {
    if (row.hierarchyPath) return row.hierarchyPath;
    if (this.type === 'CATEGORY') {
      if (row.parentId || row.parentName) {
        return row.parentName || '-';
      }
      return [row.crmClientName, row.crmAccountName].filter(Boolean).join(' > ') || '-';
    }
    return row.parentName || '-';
  }

  relationshipKey(row: WarehousingMasterData): number | string | null {
    if (!this.showRelationshipColumn) {
      return null;
    }
    if (this.type === 'CATEGORY') {
      if (row.parentId) return `category:${row.parentId}`;
      const clientId = row.crmClientId || '';
      const accountId = row.crmAccountId || '';
      if (clientId || accountId) {
        return `scope:${clientId}:${accountId}`;
      }
      const label = this.relationshipLabel(row);
      return label === '-' ? null : label;
    }
    return row.parentId || row.categoryId || row.brandId || null;
  }

  rowDepth(row: SetupListRow): number {
    return row.treeDepth || 0;
  }

  rowTypeLabel(row: SetupListRow): string {
    return this.type === 'CATEGORY' && this.rowDepth(row) > 0 ? 'Child category' : this.singularTitle;
  }

  canAddChildCategory(row: SetupListRow): boolean {
    return this.type === 'CATEGORY' && !!row.id;
  }

  rowViewRoute(row: SetupListRow): string {
    return `${this.listRoute}/view/${row.id}`;
  }

  rowEditRoute(row: SetupListRow): string {
    return `${this.listRoute}/edit/${row.id}`;
  }

  rowActionQuery(row: SetupListRow): Record<string, string> | null {
    return null;
  }

  addChildQuery(row: SetupListRow): Record<string, string> {
    return { parentId: String(row.id) };
  }

  rowStatus(row: WarehousingMasterData): string {
    return row.isActive === false ? 'INACTIVE' : 'ACTIVE';
  }

  tableMinWidth(): string {
    if (this.type === 'WAREHOUSE') return '64rem';
    if (this.type === 'REGION') return '58rem';
    if (this.showLocationColumn) return '64rem';
    if (this.showRelationshipColumn) return '52rem';
    return '44rem';
  }

  emptyColspan(): number {
    if (this.type === 'WAREHOUSE') return 5;
    return 4 + (this.type === 'REGION' ? 1 : 0) + (this.showRelationshipColumn ? 1 : 0);
  }

  private matchesFilter(value: string | number | null | undefined, filter: string | null | undefined): boolean {
    const normalizedFilter = String(filter || '').trim().toLowerCase();
    if (!normalizedFilter) {
      return true;
    }
    return String(value || '').toLowerCase().includes(normalizedFilter);
  }

  private matchesRelationship(row: WarehousingMasterData, relationship: number | string | null | undefined): boolean {
    if (relationship === null || relationship === undefined || relationship === '') {
      return true;
    }
    return this.relationshipKey(row) === relationship;
  }

  private buildDisplayRows(rows: WarehousingMasterData[]): SetupListRow[] {
    if (this.type !== 'CATEGORY') {
      return rows;
    }
    const childrenByParent = new Map<number, WarehousingMasterData[]>();
    const rowIds = new Set(rows.map(row => row.id).filter((id): id is number => !!id));
    rows.forEach(child => {
      const parentId = child.parentId;
      if (!parentId) {
        return;
      }
      const children = childrenByParent.get(parentId) || [];
      children.push(child);
      childrenByParent.set(parentId, children);
    });

    const output: SetupListRow[] = [];
    const appendRow = (row: WarehousingMasterData, depth: number, parentName?: string): void => {
      const children = (row.id ? childrenByParent.get(row.id) : []) || [];
      output.push({ ...row, treeDepth: depth, childCount: children.length, parentName: row.parentName || parentName });
      children
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach(child => appendRow(child, depth + 1, row.name));
    };

    rows
      .filter(row => !row.parentId || !rowIds.has(row.parentId))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(row => appendRow(row, 0));

    return output;
  }

  private buildRelationshipOptions(sourceRows: WarehousingMasterData[]): RelationshipFilterOption[] {
    if (!this.showRelationshipColumn) {
      return [];
    }
    const options = new Map<number | string, string>();
    sourceRows.forEach(row => {
      if (row.id) {
        options.set(row.id, row.name || row.code || `#${row.id}`);
      }
    });
    this.rows.forEach(row => {
      const value = this.relationshipKey(row);
      const label = this.relationshipLabel(row);
      if (value !== null && value !== undefined && label !== '-' && !options.has(value)) {
        options.set(value, label);
      }
    });
    return Array.from(options.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private buildValueOptions(values: (string | undefined)[], labelFor = (value: string) => value): RelationshipFilterOption[] {
    return [...new Set(values.filter((value): value is string => !!value && value !== '-'))]
      .map(value => ({ value, label: labelFor(value) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private matchesStatus(row: WarehousingMasterData, status: string | null | undefined): boolean {
    if (!status) {
      return true;
    }
    return this.rowStatus(row) === status;
  }
}
